/**
 * Assigned program detail. Ported from the Next app's
 * workouts/program/[id]/page.tsx. Loads the client's programs (full nested
 * workout tree) from GET /api/workouts/programs and shows the matching one,
 * with each workout day and a Start link into the logger. Luxury design system.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router';
import { ArrowLeft, Timer, Play } from 'lucide-react';
import { api } from '@/lib/api';

interface Workout {
  id: string;
  name: string;
  day_label: string;
  workout_type: string;
  estimated_minutes: number;
  sort_order: number;
  workout_exercises: Array<{
    id: string;
    sort_order: number;
    rest_seconds: number;
    exercises: { name: string; muscle_group: string; equipment: string };
    workout_sets: Array<{ set_number: number; target_reps: number; target_weight: number; is_warmup: boolean }>;
  }>;
  workout_cardio_blocks: Array<{ id: string; cardio_machines: { name: string }; target_minutes: number }>;
}

interface Program {
  id: string;
  name: string;
  description: string | null;
  duration_weeks: number;
  workouts_per_week: number;
  difficulty: string;
  workouts: Workout[];
}

export default function ProgramDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [program, setProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ programs: Program[] }>('/api/workouts/programs')
      .then((data) => {
        const found = data.programs?.find((p) => p.id === id);
        if (found) setProgram(found);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="px-5 pt-[max(16px,env(safe-area-inset-top))]">
        {[1, 2, 3].map((i) => (
          <div key={i} className="card animate-pulse h-32 mb-3" style={{ background: 'var(--surface)' }} />
        ))}
      </div>
    );
  }

  if (!program) {
    return (
      <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] text-center py-20">
        <p className="font-semibold" style={{ color: 'var(--cream)' }}>Program not found</p>
        <button onClick={() => navigate(-1)} className="btn-outline mt-4">Go Back</button>
      </div>
    );
  }

  return (
    <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] pb-4">
      <div className="flex items-center gap-3 mb-2">
        <button onClick={() => navigate(-1)} className="p-1" style={{ color: 'var(--champagne)' }} aria-label="Back">
          <ArrowLeft size={22} />
        </button>
        <h1 className="display" style={{ fontSize: '1.9rem' }}>{program.name}</h1>
      </div>

      {program.description && (
        <p className="text-sm mb-4 ml-8" style={{ color: 'var(--cream-muted)' }}>{program.description}</p>
      )}

      <div className="flex gap-2 mb-5 ml-8">
        <span className="text-xs px-3 py-1 font-medium" style={{ borderRadius: 'var(--radius-sm)', background: 'rgba(201,168,106,0.15)', color: 'var(--champagne)' }}>
          {program.difficulty}
        </span>
        <span className="text-xs px-3 py-1" style={{ borderRadius: 'var(--radius-sm)', background: 'var(--surface)', color: 'var(--cream-muted)' }}>
          {program.workouts_per_week}x/week
        </span>
        <span className="text-xs px-3 py-1" style={{ borderRadius: 'var(--radius-sm)', background: 'var(--surface)', color: 'var(--cream-muted)' }}>
          {program.duration_weeks} weeks
        </span>
      </div>

      <div className="flex flex-col gap-3">
        {[...program.workouts]
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((workout, i) => (
            <div key={workout.id} className="card">
              <div className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                  style={{ background: 'var(--champagne)', color: 'var(--ink)' }}
                >
                  {i + 1}
                </div>
                <div className="flex-1">
                  <p className="font-semibold" style={{ color: 'var(--cream)' }}>{workout.name}</p>
                  <div className="flex items-center gap-3 mt-0.5 text-xs" style={{ color: 'var(--cream-muted)' }}>
                    {workout.day_label && <span>{workout.day_label}</span>}
                    <span className="flex items-center gap-1"><Timer size={10} /> {workout.estimated_minutes} min</span>
                    <span>{workout.workout_exercises?.length || 0} exercises</span>
                  </div>

                  {workout.workout_exercises?.slice(0, 3).map((we) => (
                    <div key={we.id} className="flex items-center gap-2 mt-2 text-sm" style={{ color: 'var(--cream-dim)' }}>
                      <span style={{ color: 'var(--cream-muted)' }}>&bull;</span>
                      <span>{we.exercises?.name}</span>
                      {we.workout_sets?.length > 0 && (
                        <span className="text-xs" style={{ color: 'var(--cream-muted)' }}>
                          {we.workout_sets.length}&times;{we.workout_sets[0]?.target_reps}
                        </span>
                      )}
                    </div>
                  ))}

                  <Link
                    to={`/workouts/log?workout=${workout.id}&program=${program.id}`}
                    className="btn-gold w-full mt-3 text-sm py-2 flex items-center justify-center gap-2"
                  >
                    <Play size={14} fill="currentColor" /> Start
                  </Link>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
